import { Injectable } from '@angular/core';
import { resetOnLeavingRecruitment } from './recruitment-scope';

/** "CV" filter of the pipeline board; it reads the AI screening result (see the board component's class doc). */
export type BoardCvFilter = 'any' | 'screened' | 'unscreened';
/** Board/List toggle of the pipeline board. */
export type BoardView = 'board' | 'list';

/** Everything the board restores when the recruiter comes back to the same job. */
export interface RecruitmentBoardState {
  readonly searchText: string;
  /** `ApplicationSource` value, or 0 for "all sources". */
  readonly sourceFilter: number;
  /** Minimum AI score, or 0 for "any score". */
  readonly minScore: number;
  readonly cvFilter: BoardCvFilter;
  readonly view: BoardView;
  /**
   * Horizontal scroll offset of the column strip, exactly as the element reported it. Stored raw rather than as a
   * stage index because the value is written straight back onto the same element in the same writing direction —
   * which is what keeps it correct in RTL, where browsers report this offset as zero or negative.
   */
  readonly scrollLeft: number;
}

/**
 * Board view state per job, so that Back from a candidate profile returns to the board the recruiter left.
 *
 * The design's `Interaction notes` artboard says "The board keeps scroll and filter state so Back returns to the
 * same column" (SP4c Task 4, D3). This is deliberately a route-scoped store and not query parameters: the design
 * asks for state that survives Back, not for shareable filtered links, and query parameters would rewrite the URL
 * on every keystroke in the search box.
 *
 * Keyed by `jobRequisitionId`, so one job's filters never decide what another job's board shows — a board opened
 * for a job with no remembered state starts unfiltered. Same lifetime as the other recruitment stores: provided on
 * the `recruitment` route and dropped when a navigation leaves Recruitment, so the next visit (or the next user,
 * since login does not reload the page) starts clean.
 */
@Injectable()
export class RecruitmentBoardStateStore {
  private readonly byJob = new Map<string, RecruitmentBoardState>();

  constructor() {
    resetOnLeavingRecruitment(() => this.reset());
  }

  /** The remembered state for this job, or `null` when this board has not been visited in this Recruitment visit. */
  get(jobRequisitionId: string): RecruitmentBoardState | null {
    if (!jobRequisitionId) return null;
    return this.byJob.get(jobRequisitionId) ?? null;
  }

  save(jobRequisitionId: string, state: RecruitmentBoardState): void {
    if (!jobRequisitionId) return;
    this.byJob.set(jobRequisitionId, state);
  }

  reset(): void {
    this.byJob.clear();
  }
}
